/**
 * Export Modal - Settings UI and progress display
 */

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Cancel01Icon as X, Download01Icon as Download, Tick01Icon as Check, AlertCircleIcon as AlertCircle, ArrowDown01Icon as ChevronDown, Film01Icon as Clapperboard, MusicNote01Icon as Music } from 'hugeicons-react'
import { useExportStore } from '@/store/export-store'
import { useEditorStore } from '@/store/editor-store'
import { useRecordingStore } from '@/store/recording-store'
import { estimateFileSize, formatFileSize, isWebCodecsSupported, exportAudio, downloadBlob } from '@/lib/export'
import { exportGif } from '@/lib/export/gif-encoder'

const RESOLUTIONS = [
    { label: 'Original', value: 'original' },
    { label: '4k (3840×2160)', value: '4k', width: 3840, height: 2160 },
    { label: '1080p (1920×1080)', value: '1080p', width: 1920, height: 1080 },
    { label: '720p (1280×720)', value: '720p', width: 1280, height: 720 },
    { label: '480p (854×480)', value: '480p', width: 854, height: 480 },
]

const FORMATS = [
    { label: 'MP4 (H.264)', value: 'mp4', description: 'Best compatibility' },
    { label: 'WebM (VP9)', value: 'webm', description: 'Great for web/transparency' },
]

const FRAME_RATES = [
    { label: '24 fps', value: 24 },
    { label: '30 fps', value: 30 },
    { label: '60 fps', value: 60 },
]

const QUALITY_PRESETS = [
    { label: 'Low', bitrate: 4_000_000, description: 'Smaller file' },
    { label: 'Medium', bitrate: 8_000_000, description: 'Balanced' },
    { label: 'High', bitrate: 18_000_000, description: 'Great quality' },
    { label: 'Maximum', bitrate: 50_000_000, description: 'Lossless-like' },
]

function CustomSelect({
    label,
    value,
    options,
    onChange,
    className = "",
    placeholder = "Select..."
}: {
    label: string,
    value: string | null,
    options: { label: string, value: string }[],
    onChange: (val: string) => void,
    className?: string,
    placeholder?: string
}) {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 })

    useEffect(() => {
        if (isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            setCoords({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            })
        }
    }, [isOpen])

    return (
        <div className={`space-y-1 relative ${className}`} ref={containerRef}>
            <label className="text-[10px] font-bold text-txt-dim uppercase tracking-wider ml-1">
                {label}
            </label>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full bg-surface-800 border ${isOpen ? 'border-brand-500/50' : 'border-white/10'} rounded-xl text-left px-3 py-2 outline-none hover:bg-white/5 transition-all cursor-pointer flex items-center justify-between group`}
            >
                <span className="text-xs text-white truncate font-medium">
                    {options.find(o => o.value === value)?.label || placeholder}
                </span>
                <ChevronDown size={14} className={`text-txt-dim group-hover:text-white transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && createPortal(
                <>
                    <div className="fixed inset-0 z-[1000]" onClick={() => setIsOpen(false)} />
                    <motion.div
                        initial={{ opacity: 0, y: 4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ duration: 0.1 }}
                        style={{
                            position: 'absolute',
                            top: coords.top + 8,
                            left: coords.left,
                            width: coords.width,
                            zIndex: 1001
                        }}
                        className="bg-surface-800 border border-white/10 rounded-xl shadow-2xl overflow-hidden py-1.5 glass-panel"
                    >
                        <div className="max-h-[240px] overflow-y-auto custom-scrollbar">
                            {options.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                        onChange(opt.value)
                                        setIsOpen(false)
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm transition-all flex items-center justify-between ${value === opt.value
                                        ? 'text-brand-400 bg-brand-500/10 font-semibold'
                                        : 'text-txt-secondary hover:text-white hover:bg-white/5'
                                        }`}
                                >
                                    <span>{opt.label}</span>
                                    {value === opt.value && <Check size={14} className="text-brand-400" />}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                </>,
                document.body
            )}
        </div>
    )
}

export default function ExportModal() {
    const {
        isModalOpen,
        isExporting,
        progress,
        error,
        settings,
        closeModal,
        setSettings,
        startExport,
        cancelExport,
    } = useExportStore()

    const duration = useEditorStore(state => state.duration)
    const originalFrameRate = useRecordingStore(state => state.originalFrameRate)
    const [isCustomFPS, setIsCustomFPS] = useState(false)
    const [selectedResolution, setSelectedResolution] = useState('1080p')
    const [selectedQuality, setSelectedQuality] = useState('High')
    const [isExportingGif, setIsExportingGif] = useState(false)
    const [gifProgress, setGifProgress] = useState<string | null>(null)
    const [gifError, setGifError] = useState<string | null>(null)
    const [isExportingAudio, setIsExportingAudio] = useState(false)
    const [audioError, setAudioError] = useState<string | null>(null)

    // Sync settings with original recording properties when modal opens
    useEffect(() => {
        if (isModalOpen && originalFrameRate && !isCustomFPS) {
            setSettings({ frameRate: Math.round(originalFrameRate) })
        }
    }, [isModalOpen, originalFrameRate, isCustomFPS, setSettings])

    if (!isModalOpen) return null

    const supported = isWebCodecsSupported()
    const estimatedSize = estimateFileSize(settings, duration)

    const handleResolutionChange = (value: string) => {
        setSelectedResolution(value)
        const resolution = RESOLUTIONS.find(r => r.value === value)
        if (resolution && resolution.width) {
            setSettings({ resolution: { width: resolution.width, height: resolution.height! } })
        }
    }

    const handleQualityChange = (label: string) => {
        setSelectedQuality(label)
        const quality = QUALITY_PRESETS.find(q => q.label === label)
        if (quality) {
            setSettings({ videoBitrate: quality.bitrate })
        }
    }

    const handleExportAudio = async () => {
        setIsExportingAudio(true)
        setAudioError(null)
        try {
            const blob = await exportAudio()
            if (!blob) {
                setAudioError('No audio track found in this recording.')
                return
            }
            downloadBlob(blob, 'recording.wav')
        } catch (err: any) {
            setAudioError(err?.message ?? 'Audio export failed')
        } finally {
            setIsExportingAudio(false)
        }
    }

    const handleExportGif = async () => {
        const res = RESOLUTIONS.find(r => r.value === selectedResolution)
        const gifWidth = res?.width ?? 1280
        const gifHeight = res?.height ?? 720
        setIsExportingGif(true)
        setGifError(null)
        setGifProgress('Starting GIF export…')
        try {
            const blob = await exportGif(
                { width: gifWidth, height: gifHeight, frameRate: 12 },
                (p) => setGifProgress(p.message ?? null)
            )
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'recording.gif'
            a.click()
            URL.revokeObjectURL(url)
            setGifProgress('GIF downloaded!')
            setTimeout(() => setGifProgress(null), 3000)
        } catch (err: any) {
            setGifError(err?.message ?? 'GIF export failed')
        } finally {
            setIsExportingGif(false)
        }
    }

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={!isExporting ? closeModal : undefined}
                    className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                />

                {/* Modal */}
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="relative bg-surface-900 border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-white/5 shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-brand-500/20 text-brand-400 flex items-center justify-center">
                                <Download size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-white">Export Video</h2>
                                <p className="text-xs text-txt-dim">
                                    {duration ? `${duration.toFixed(1)}s video` : 'Configure export settings'}
                                </p>
                            </div>
                        </div>
                        {!isExporting && (
                            <button
                                onClick={closeModal}
                                className="p-2 hover:bg-white/5 rounded-lg text-txt-dim hover:text-white transition-colors"
                            >
                                <X size={20} />
                            </button>
                        )}
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6 overflow-y-auto custom-scrollbar">
                        {/* WebCodecs Check */}
                        {!supported && (
                            <div className="p-4 bg-danger-500/10 border border-danger-500/20 rounded-xl flex items-start gap-3">
                                <AlertCircle className="text-danger-400 shrink-0 mt-0.5" size={18} />
                                <div>
                                    <p className="text-sm text-danger-300 font-medium">Browser Not Supported</p>
                                    <p className="text-xs text-danger-400/80 mt-1">
                                        Your browser doesn't support WebCodecs. Please use Chrome, Edge, or a Chromium-based browser.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Export Progress */}
                        {isExporting && progress && (
                            <div className="space-y-4">
                                {/* Progress Bar */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-txt-secondary">{progress.message}</span>
                                        <span className="text-white font-medium">{Math.round(progress.progress)}%</span>
                                    </div>
                                    <div className="h-2 bg-surface-800 rounded-full overflow-hidden">
                                        <motion.div
                                            className="h-full bg-gradient-to-r from-brand-500 to-brand-400 rounded-full"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${progress.progress}%` }}
                                            transition={{ duration: 0.3 }}
                                        />
                                    </div>
                                </div>

                                {/* Stats */}
                                {progress.stage === 'rendering' && (
                                    <div className="grid grid-cols-2 gap-4 p-4 bg-surface-800/50 rounded-xl">
                                        <div>
                                            <div className="text-xs text-txt-dim">Frames</div>
                                            <div className="text-sm text-white font-medium">
                                                {progress.currentFrame} / {progress.totalFrames}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-txt-dim">Time Remaining</div>
                                            <div className="text-sm text-white font-medium">
                                                ~{progress.estimatedTimeRemaining}s
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Cancel Button */}
                                <button
                                    onClick={cancelExport}
                                    className="w-full py-2.5 px-4 rounded-xl border border-white/10 text-txt-secondary hover:text-white hover:bg-white/5 transition-colors text-sm font-medium"
                                >
                                    Cancel Export
                                </button>
                            </div>
                        )}

                        {/* Settings (hidden during export) */}
                        {!isExporting && (
                            <>
                                {/* Main Settings Grid (3 columns) */}
                                <div className="grid grid-cols-3 gap-3">
                                    <CustomSelect
                                        label="Format"
                                        value={settings.format}
                                        options={FORMATS.map(f => ({ label: f.label.split(' ')[0], value: f.value }))}
                                        onChange={(val) => setSettings({ format: val as any })}
                                    />

                                    <CustomSelect
                                        label="Res"
                                        value={selectedResolution}
                                        options={RESOLUTIONS.map(r => ({ label: r.label.split(' ')[0], value: r.value }))}
                                        onChange={handleResolutionChange}
                                    />

                                    <CustomSelect
                                        label="Quality"
                                        value={selectedQuality}
                                        options={QUALITY_PRESETS.map(q => ({ label: q.label, value: q.label }))}
                                        onChange={handleQualityChange}
                                    />
                                </div>

                                {/* Framing & Audio Section */}
                                <div className="space-y-5 p-4 bg-surface-800/40 rounded-xl border border-white/5">
                                    {/* Frame Rate Header & Toggle */}
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div className="space-y-0.5">
                                                <label className="text-[10px] font-bold text-txt-dim uppercase tracking-wider block">Frame Rate</label>
                                                <p className="text-xs text-txt-secondary">
                                                    {isCustomFPS ? 'Choose target FPS' : `Matches original (${Math.round(originalFrameRate || 30)} FPS)`}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-[10px] font-medium text-txt-dim uppercase tracking-widest leading-none">Custom</span>
                                                <button
                                                    onClick={() => setIsCustomFPS(!isCustomFPS)}
                                                    className={`w-10 h-5 rounded-full relative transition-all duration-300 ${isCustomFPS ? 'bg-brand-500' : 'bg-white/10'}`}
                                                >
                                                    <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm ${isCustomFPS ? 'left-6' : 'left-1'}`} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* FPS Options (shown if custom) */}
                                        <AnimatePresence>
                                            {isCustomFPS && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="flex gap-2 pt-1 border-t border-white/5 mt-3">
                                                        {FRAME_RATES.map((fr) => (
                                                            <button
                                                                key={fr.value}
                                                                onClick={() => setSettings({ frameRate: fr.value })}
                                                                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold border transition-all ${settings.frameRate === fr.value
                                                                    ? 'bg-brand-500/10 border-brand-500/30 text-brand-300'
                                                                    : 'border-white/10 hover:bg-white/5 text-txt-secondary'
                                                                    }`}
                                                            >
                                                                {fr.value} FPS
                                                            </button>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Divider */}
                                    <div className="h-px bg-white/5" />

                                    {/* Include Audio */}
                                    <div className="flex items-center justify-between">
                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-txt-dim uppercase tracking-wider block">Audio track</label>
                                            <p className="text-sm text-white/90 font-medium">Include Audio</p>
                                        </div>
                                        <button
                                            onClick={() => setSettings({ includeAudio: !settings.includeAudio })}
                                            className={`w-10 h-5 rounded-full relative transition-all duration-300 ${settings.includeAudio ? 'bg-brand-500' : 'bg-white/10'}`}
                                        >
                                            <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm ${settings.includeAudio ? 'left-6' : 'left-1'}`} />
                                        </button>
                                    </div>
                                </div>

                                {/* Estimated Size */}
                                <div className="flex items-center justify-between p-3 bg-surface-800/30 rounded-lg">
                                    <span className="text-xs text-txt-dim">Estimated file size</span>
                                    <span className="text-sm text-white font-medium">{formatFileSize(estimatedSize)}</span>
                                </div>

                                {/* Error Display */}
                                {error && (
                                    <div className="p-3 bg-danger-500/10 border border-danger-500/20 rounded-lg flex items-center gap-2">
                                        <AlertCircle size={16} className="text-danger-400" />
                                        <span className="text-sm text-danger-300">{error}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    {!isExporting && (
                        <div className="p-6 border-t border-white/5 space-y-3">
                            {/* GIF status/error */}
                            {gifProgress && (
                                <div className="text-xs text-white/50 text-center animate-pulse">{gifProgress}</div>
                            )}
                            {gifError && (
                                <div className="flex items-center gap-2 p-2 bg-danger-500/10 border border-danger-500/20 rounded-lg">
                                    <AlertCircle size={14} className="text-danger-400 shrink-0" />
                                    <span className="text-xs text-danger-300">{gifError}</span>
                                </div>
                            )}
                            {audioError && (
                                <div className="flex items-center gap-2 p-2 bg-danger-500/10 border border-danger-500/20 rounded-lg">
                                    <AlertCircle size={14} className="text-danger-400 shrink-0" />
                                    <span className="text-xs text-danger-300">{audioError}</span>
                                </div>
                            )}
                            <div className="flex gap-3">
                                <button
                                    onClick={closeModal}
                                    className="py-2.5 px-4 rounded-xl border border-white/10 text-txt-secondary hover:text-white hover:bg-white/5 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleExportAudio}
                                    disabled={isExportingAudio}
                                    title="Export audio track as WAV"
                                    className="py-2.5 px-4 rounded-xl border border-white/10 hover:border-violet-500/40 text-txt-secondary hover:text-violet-300 hover:bg-violet-500/5 transition-colors font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <Music size={15} />
                                    {isExportingAudio ? 'Exporting…' : 'WAV'}
                                </button>
                                <button
                                    onClick={handleExportGif}
                                    disabled={isExportingGif}
                                    title="Export as animated GIF (requires internet to load encoder)"
                                    className="py-2.5 px-4 rounded-xl border border-white/10 hover:border-emerald-500/40 text-txt-secondary hover:text-emerald-300 hover:bg-emerald-500/5 transition-colors font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <Clapperboard size={15} />
                                    {isExportingGif ? 'Exporting…' : 'GIF'}
                                </button>
                                <button
                                    onClick={startExport}
                                    disabled={!supported}
                                    className="flex-1 py-2.5 px-4 rounded-xl bg-brand-500 hover:bg-brand-400 text-white font-medium transition-colors shadow-lg shadow-brand-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    <Download size={16} />
                                    Export Video
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Complete State */}
                    {progress?.stage === 'complete' && (
                        <div className="p-6 border-t border-white/5 space-y-4">
                            <div className="flex items-center gap-3 p-4 bg-success-500/10 border border-success-500/20 rounded-xl">
                                <Check className="text-success-400" size={20} />
                                <div>
                                    <p className="text-sm text-success-300 font-medium">Export Complete!</p>
                                    <p className="text-xs text-success-400/80">Your video is ready to save.</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => useExportStore.getState().downloadLastExport()}
                                    className="flex-1 py-2.5 px-4 rounded-xl bg-brand-500 hover:bg-brand-400 text-white font-medium transition-colors shadow-lg shadow-brand-500/20 flex items-center justify-center gap-2"
                                >
                                    <Download size={16} />
                                    Download Video
                                </button>
                                <button
                                    onClick={closeModal}
                                    className="flex-1 py-2.5 px-4 rounded-xl bg-surface-800 hover:bg-surface-700 text-white font-medium transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    )
}
